import { UIChunkType } from '@myre/app-agent-domain';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';

import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { isDataUIPart } from 'ai';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  type ClarificationAnswer,
  type MyreUiMessagePart,
  type NavigateProposal,
} from '../../interfaces/chat.interfaces';

/**
 * Renders an agent-proposed destination as a LINK offered beside the answer.
 *
 * It is not a door: the turn that proposed it has already delivered its answer,
 * and nothing is waiting on this card. The user opens the page, stays, or leaves
 * it alone — and the conversation is finished either way. A "don't ask again"
 * checkbox, bound to the current chat, opens future destinations straight away
 * without rendering the card.
 *
 * The click still SPEAKS to the agent, which is the whole reason it emits
 * anything: `value` is the sentence the agent reads ("j'ai ouvert la page « X »"
 * — with the routing OUTCOME, not merely the intent), `label` is the short user
 * bubble. `chat.component` sends both, so the agent gets a next turn it can pick
 * up from ("vous êtes sur l'écran X, voulez-vous que je …").
 *
 * Only `streaming()` disables the buttons, and only because a click mid-answer
 * would start the next turn on top of the one still arriving.
 */
@Component({
  selector: 'app-navigate-confirm',
  standalone: true,
  templateUrl: './navigate-confirm.component.html',
  styleUrl: './navigate-confirm.component.scss',
})
export class NavigateConfirmComponent {
  public readonly part = input.required<MyreUiMessagePart>();
  public readonly streaming = input<boolean>(false);
  /** Session-bound preference: when true, accept + navigate without asking. */
  public readonly skipConfirm = input<boolean>(false);

  /** The user's decision, sent to the agent as the conversation's next message. */
  public readonly decisionMade = output<ClarificationAnswer>();
  /** Raised when the user (un)ticks "don't ask again for this chat". */
  public readonly skipConfirmChange = output<boolean>();

  private readonly _router = inject(Router);
  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get _w() {
    return this._wordings._library.chat;
  }

  protected readonly _decided = signal(false);
  protected readonly _dontAskAgain = signal(false);

  protected readonly _proposal = computed<NavigateProposal | null>(() => {
    const part = this.part();

    if (!isDataUIPart(part) || part.type !== UIChunkType.DataNavigate) {
      return null;
    }

    return (part.data as { navigate?: NavigateProposal }).navigate ?? null;
  });

  /**
   * Whether the proposal is resolved: a live decision (`_decided`) wins; otherwise,
   * on a reload, the persisted `decided` flag locks the card — and crucially stops
   * the "don't ask again" effect below from re-navigating on every reload.
   */
  protected readonly _resolvedDecided = computed<boolean>(
    () => this._decided() || (this._proposal()?.decided ?? false),
  );

  constructor() {
    // Honour a standing "don't ask again": navigate straight away (once) and
    // report the implicit acceptance back to the agent. A reloaded, already-decided
    // proposal must NOT re-fire — `_resolvedDecided` reads the persisted flag.
    effect(() => {
      const proposal = this._proposal();

      if (!proposal || this._resolvedDecided() || !this.skipConfirm()) {
        return;
      }

      void this._accept(proposal, false);
    });
  }

  protected get _locked(): boolean {
    return this.streaming() || this._resolvedDecided();
  }

  protected _onAccept(): void {
    const proposal = this._proposal();

    if (!proposal || this._locked) {
      return;
    }

    void this._accept(proposal, this._dontAskAgain());
  }

  protected _onDecline(): void {
    const proposal = this._proposal();

    if (!proposal || this._locked) {
      return;
    }

    this._decided.set(true);
    this.decisionMade.emit({
      value: this._w.navigateDeclinedMessage({
        bindings: { label: proposal.label },
      }),
      label: this._w.navigateStayHere(),
    });
  }

  protected _onToggleDontAskAgain(checked: boolean): void {
    this._dontAskAgain.set(checked);
    this.skipConfirmChange.emit(checked);
  }

  private async _accept(
    proposal: NavigateProposal,
    fromCard: boolean,
  ): Promise<void> {
    this._decided.set(true);

    // The sentence sent to the agent carries the navigation OUTCOME, not just
    // the intent: navigateByUrl resolves false when a guard blocks the route,
    // or rejects on a routing error. The caught error message is surfaced in the
    // result so the agent learns WHY the page didn't open, not just that it didn't.
    const outcome = await this._router
      .navigateByUrl(proposal.url)
      .then(ok => ({ ok, error: null as string | null }))
      .catch((error: unknown) => ({
        ok: false,
        error: error instanceof Error ? error.message : String(error),
      }));

    const reason = outcome.error
      ? ` : ${outcome.error}`
      : this._w.navigateRouteInaccessible();

    const value = outcome.ok
      ? this._w.navigateOpenedMessage({ bindings: { label: proposal.label } })
      : this._w.navigateOpenFailedMessage({
          bindings: { reason, label: proposal.label },
        });

    this.decisionMade.emit({
      value,
      label: fromCard
        ? this._w.navigateOpenLabel({ bindings: { label: proposal.label } })
        : this._w.navigationLabel({ bindings: { label: proposal.label } }),
    });
  }
}
